import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FileToUpload, IBackendResponse, TicketCommentToSubmit, TicketToSubmit } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { HttpResponseHandleHelper } from '../http/http-response-handle-helper';

@Injectable({
  providedIn: 'root',
})
export class TicketDialogService {
  readonly #http = inject(HttpClient);

  public upload(file: FileToUpload): Observable<IBackendResponse<boolean>> {
    const formData = new FormData();
    formData.append('file', file.fileData);
    file.uploadInProgress = true;
    return this.#http
      .post<any>(`${environment.serverlessApihost + environment.ticketdialog}/uploadfile`, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              file.uploadProgress = Math.round((event.loaded * 100) / event.total);
              break;
            case HttpEventType.Response:
              file.uploadInProgress = false;
              if (event.ok) {
                file.fileRefForTicket = event.body.fileRefForTicket;
                file.uploadProgress = 100;
                return {
                  success: true,
                  data: true,
                } as IBackendResponse<boolean>;
              } else {
                return {
                  success: false,
                  data: false,
                } as IBackendResponse<boolean>;
              }
            default:
              break;
          }
          return undefined;
        }),
        filter((result) => typeof result !== 'undefined'),

        catchError((error: HttpErrorResponse) => {
          file.uploadInProgress = false;
          const backendError = HttpResponseHandleHelper.getBackendErrorFromHttpError(error);
          const messageFromError = HttpResponseHandleHelper.getMessageFromError(backendError);
          file.uploadError = messageFromError;
          return of({
            success: false,
            message: messageFromError,
            err: backendError,
          } as IBackendResponse<boolean>);
        }),
      );
  }

  public submitTicket(ticketData: TicketToSubmit): Observable<IBackendResponse<boolean>> {
    return HttpResponseHandleHelper.handleResponse(
      this.#http.post<boolean>(`${environment.apihost + environment.ticketdialog}/saveticket`, ticketData, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    );
  }

  public submitComment(commentData: TicketCommentToSubmit): Observable<IBackendResponse<boolean>> {
    return HttpResponseHandleHelper.handleResponse(
      this.#http.post<boolean>(`${environment.apihost + environment.ticketdialog}/savecomment`, commentData, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    );
  }

  public delete(fileToDelete: FileToUpload): Observable<IBackendResponse<boolean>> {
    if (!fileToDelete.fileRefForTicket || fileToDelete.uploadError) {
      // seems that the file wasn't upload successfully before, so we don't send a request to the server
      return of({
        success: true,
        data: true,
      });
    }

    return HttpResponseHandleHelper.handleResponse(
      this.#http.post<boolean>(`${environment.apihost + environment.ticketdialog}/deletefile`, fileToDelete.fileRefForTicket, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    );
  }

  public updateTicketStatus(ticketId: string, status: 'Closed' | 'Open'): Observable<IBackendResponse<boolean>> {
    return HttpResponseHandleHelper.handleResponse(
      this.#http.post<boolean>(`${environment.apihost + environment.ticketdialog}/ticket/${ticketId}/status`, status, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    );
  }
}
